import { parseFormula } from './formula';
import {
  add,
  div,
  isZero,
  mul,
  neg,
  ONE,
  rational,
  sub,
  toSmallestIntegers,
  ZERO,
  type Rational,
} from './rational';

/**
 * Balances a chemical equation exactly.
 *
 * Each element becomes a row of a matrix and each species a column, with
 * reactants positive and products negative. A charge row is added whenever any
 * species carries one. Balancing is then finding the nullspace, done with exact
 * rational arithmetic so a coefficient is either a whole number or it is not.
 *
 * Where there is no solution, or more than one independent one, it says so
 * instead of picking an answer.
 */

export class BalanceError extends Error {}

export interface ParsedEquation {
  reactants: string[];
  products: string[];
}

export type BalanceResult =
  | {
      ok: true;
      coefficients: bigint[];
      reactants: string[];
      products: string[];
      /** The balanced equation written out. */
      equation: string;
      hasCharges: boolean;
    }
  | { ok: false; reason: 'no-solution' | 'multiple-solutions' | 'invalid'; message: string };

const ARROWS = /(<=>|<->|-->|->|→|⟶|⇌|=>|=)/;

export function parseEquation(input: string): ParsedEquation {
  const text = (input ?? '').trim();
  if (!text) throw new BalanceError('No equation was given.');

  const parts = text.split(ARROWS);
  if (parts.length < 3) {
    throw new BalanceError('An equation needs an arrow, like "H2 + O2 -> H2O".');
  }
  const [left, , ...rest] = parts;
  const right = rest.join('');

  // Species are separated by a plus with space around it, so a charge like
  // "Fe3+" is never mistaken for a separator.
  const split = (side: string) =>
    side
      .split(/\s+\+\s+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const reactants = split(left ?? '');
  const products = split(right);

  if (reactants.length === 0) throw new BalanceError('There is nothing on the left of the arrow.');
  if (products.length === 0) throw new BalanceError('There is nothing on the right of the arrow.');

  return { reactants, products };
}

interface Species {
  formula: string;
  counts: Map<string, number>;
  charge: number;
}

function readSpecies(formula: string): Species {
  const parsed = parseFormula(formula);
  // A coefficient the user already typed is ignored; balancing supplies them.
  const counts = new Map<string, number>();
  for (const [symbol, n] of parsed.counts) counts.set(symbol, n / parsed.coefficient);
  return { formula, counts, charge: parsed.charge / parsed.coefficient };
}

/** Reduced row echelon form, exactly. */
function rref(matrix: Rational[][]): { matrix: Rational[][]; pivots: number[] } {
  const rows = matrix.length;
  const cols = matrix[0]?.length ?? 0;
  const m = matrix.map((row) => row.slice());
  const pivots: number[] = [];
  let row = 0;

  for (let col = 0; col < cols && row < rows; col += 1) {
    let target = -1;
    for (let r = row; r < rows; r += 1) {
      if (!isZero(m[r]![col]!)) {
        target = r;
        break;
      }
    }
    if (target === -1) continue;

    const swap = m[row]!;
    m[row] = m[target]!;
    m[target] = swap;

    const lead = m[row]![col]!;
    m[row] = m[row]!.map((value) => div(value, lead));

    for (let r = 0; r < rows; r += 1) {
      if (r === row) continue;
      const factor = m[r]![col]!;
      if (isZero(factor)) continue;
      m[r] = m[r]!.map((value, c) => sub(value, mul(factor, m[row]![c]!)));
    }

    pivots.push(col);
    row += 1;
  }

  return { matrix: m, pivots };
}

export function balance(input: string): BalanceResult {
  let parsed: ParsedEquation;
  try {
    parsed = parseEquation(input);
  } catch (err) {
    return { ok: false, reason: 'invalid', message: (err as Error).message };
  }

  let species: Species[];
  try {
    species = [...parsed.reactants, ...parsed.products].map(readSpecies);
  } catch (err) {
    return { ok: false, reason: 'invalid', message: (err as Error).message };
  }

  const elements = [...new Set(species.flatMap((s) => [...s.counts.keys()]))].sort();
  const hasCharges = species.some((s) => s.charge !== 0);
  const reactantCount = parsed.reactants.length;

  const rows: Rational[][] = elements.map((element) =>
    species.map((s, i) => {
      const n = s.counts.get(element) ?? 0;
      return rational(i < reactantCount ? n : -n);
    }),
  );

  if (hasCharges) {
    rows.push(species.map((s, i) => rational(i < reactantCount ? s.charge : -s.charge)));
  }

  if (species.length < 2) {
    return { ok: false, reason: 'invalid', message: 'An equation needs at least two species.' };
  }

  const { matrix, pivots } = rref(rows);
  const free = species.length - pivots.length;

  if (free === 0) {
    return {
      ok: false,
      reason: 'no-solution',
      message: 'These species cannot be balanced — the only solution is all zeros.',
    };
  }
  if (free > 1) {
    return {
      ok: false,
      reason: 'multiple-solutions',
      message: `This has ${free} independent solutions, so there is no single balanced form. It is usually two separate reactions written as one.`,
    };
  }

  // One free column: set it to 1 and back-substitute.
  const pivotSet = new Set(pivots);
  const freeColumn = [...Array(species.length).keys()].find((c) => !pivotSet.has(c))!;

  const solution: Rational[] = Array.from({ length: species.length }, () => ZERO);
  solution[freeColumn] = ONE;
  pivots.forEach((col, r) => {
    solution[col] = neg(matrix[r]![freeColumn]!);
  });

  const coefficients = toSmallestIntegers(solution);

  if (coefficients.some((c) => c === 0n)) {
    return {
      ok: false,
      reason: 'no-solution',
      message: 'No balance exists that uses every species — one of them would need a coefficient of zero.',
    };
  }

  const negatives = coefficients.filter((c) => c < 0n).length;
  const normalized =
    negatives > coefficients.length / 2 ? coefficients.map((c) => -c) : coefficients.slice();

  if (normalized.some((c) => c < 0n)) {
    return {
      ok: false,
      reason: 'no-solution',
      message:
        'No balance exists with all species on the sides given — some coefficient comes out negative, which usually means a species is on the wrong side.',
    };
  }

  return {
    ok: true,
    coefficients: normalized,
    reactants: parsed.reactants,
    products: parsed.products,
    equation: writeEquation(parsed, normalized),
    hasCharges,
  };
}

export function writeEquation(parsed: ParsedEquation, coefficients: bigint[]): string {
  const side = (formulas: string[], offset: number) =>
    formulas
      .map((formula, i) => {
        const c = coefficients[offset + i]!;
        return c === 1n ? formula : `${c} ${formula}`;
      })
      .join(' + ');

  return `${side(parsed.reactants, 0)} → ${side(parsed.products, parsed.reactants.length)}`;
}

/** Confirms a balanced equation really does balance, atom by atom and in charge. */
export function verifyBalance(
  parsed: ParsedEquation,
  coefficients: bigint[],
): { balanced: boolean; perElement: Map<string, { left: number; right: number }>; charge: { left: number; right: number } } {
  const perElement = new Map<string, { left: number; right: number }>();
  const charge = { left: 0, right: 0 };

  const walk = (formulas: string[], offset: number, sideKey: 'left' | 'right') => {
    formulas.forEach((formula, i) => {
      const c = Number(coefficients[offset + i]!);
      const species = readSpecies(formula);
      for (const [element, n] of species.counts) {
        const entry = perElement.get(element) ?? { left: 0, right: 0 };
        entry[sideKey] += n * c;
        perElement.set(element, entry);
      }
      charge[sideKey] += species.charge * c;
    });
  };

  walk(parsed.reactants, 0, 'left');
  walk(parsed.products, parsed.reactants.length, 'right');

  const balanced =
    [...perElement.values()].every((v) => v.left === v.right) && charge.left === charge.right;

  return { balanced, perElement, charge };
}

export { add };
